import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    type MapboxMap,
    MapItemType,
} from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import type { GeoJSONFeature } from 'mapbox-gl';
import { SetActiveParcel } from '../components/parcels/parcels.action';
import { AppState } from '../models/app-state';
import { Parcel } from '../models/parcel';
import { MapService } from './map.service';

@Injectable({
    providedIn: 'root',
})
export class ParcelsInformationService {
    landSourceId = 'Land';
    layerSuffixSelected = 'Selected';
    layerSuffixUnselected = 'Unselected';
    layerSuffixLabel = 'Label';

    layerIdsLabel: string[] = [];
    layerIds: string[] = [];
    layerIdsSelected: string[];
    layerIdsUnselected: string[];
    initialSelectedIds?: string | string[];
    layerIdsSelectable: string[];
    constructor(
        private consoleLogService: ConsoleLogService,
        private mapService: MapService,
        private store: Store<AppState>
    ) {}

    addSources(mapboxMap: MapboxMap): void {
        const allLayersIds = this.mapService.getLayerIds(mapboxMap);
        this.layerIds = allLayersIds.filter((id) =>
            id.includes(this.landSourceId)
        );
        this.getLayerIds(this.layerIds);
        this.setSelectedFilter(mapboxMap, this.initialSelectedIds);

        this.layerIdsSelectable = this.layerIdsUnselected;
        this.initialSelectedIds = undefined;
        this.store.dispatch(SetActiveParcel({ parcel: undefined }));
    }

    getLayerIds(layerIds: Readonly<string[]>): void {
        this.layerIdsSelected = layerIds.filter((id) =>
            id.includes(this.layerSuffixSelected)
        );
        this.layerIdsUnselected = layerIds.filter((id) =>
            id.includes(this.layerSuffixUnselected)
        );

        this.layerIdsLabel = layerIds.filter((id) =>
            id.includes(this.layerSuffixLabel)
        );

        this.layerIds = [
            ...this.layerIdsLabel,
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];

        this.consoleLogService.debug(`Land layer IDs`, this.layerIds);
    }

    setLayerVisibility(mapboxMap: MapboxMap, visible: boolean): void {
        if (!this.layerIdsUnselected) {
            return;
        }
        if (visible) {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
            this.layerIdsLabel.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });

            this.layerIdsSelected.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
        } else {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
            this.layerIdsLabel.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });

            this.layerIdsSelected.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
        }
    }

    saveParcelToCache(mapType: MapItemType, features: GeoJSONFeature[]): void {
        if (!this.isParcel(mapType) || !features || features.length === 0) {
            this.store.dispatch(SetActiveParcel({ parcel: undefined }));
            return;
        }

        const parcel = features[0].properties as Parcel;
        if (parcel) {
            this.store.dispatch(SetActiveParcel({ parcel }));
        }
    }

    isParcel = (mapType: MapItemType) =>
        mapType === 'PrivateLand' || mapType === 'MixedLand';

    setSelectedFilter(
        mapboxMap: MapboxMap,
        selectedIds?: string | string[]
    ): void {
        if (!selectedIds) {
            this.store.dispatch(SetActiveParcel({ parcel: undefined }));
        }
        if (!this.layerIds || this.layerIds.length === 0) {
            this.initialSelectedIds = selectedIds;
            return;
        }
        this.mapService.applyLayerFilters(
            mapboxMap,
            this.layerIds,
            selectedIds
        );
    }
}
